import type { ReactNode } from 'react';
import { Refused, SkeletonSet, StaleNote, type SkeletonSurface } from '@cwos/ui-admin';
import { LoadFailure } from './LoadFailure.js';
import type { ListPhase } from './listState.js';

/**
 * Renders whichever of the four non-content states a panel is in, and the
 * panel's own content when it is in none of them.
 *
 * The point is that `empty` and `failed` are separate PROPS. A caller cannot
 * supply an empty state and forget the failure state — the type will not let
 * them — which is the whole reason this exists rather than a fourth branch in
 * each screen's ternary chain.
 *
 * `stale` renders the children untouched — a failed refresh over rows that did
 * load must not blank them — AND says so (PH-UX-006). Thirty-nine call sites
 * and none of them branched on `stale`, so a supervisor reading a list
 * mid-shift had no way to tell the rows were the last answer the screen got
 * rather than the current one. The sentence lives here because it is owed by
 * every list, not by whichever caller remembered. `retainedAt`, when the
 * caller knows it, dates the rows; otherwise the note says "the last answer we
 * got" rather than inventing a time. Polling surfaces may still add their own
 * `PausedStrip` above this.
 */
export interface ListStateProps {
  phase: ListPhase;
  /** Which skeleton mirrors this panel's layout. */
  surface?: SkeletonSurface;
  rows?: number;
  /** What "there is genuinely nothing" looks like here. */
  empty: ReactNode;
  /** The sentence for a failure. Plain words about THIS panel, no codes. */
  failedMessage: string;
  onRetry?: () => void;
  /**
   * When the retained rows were fetched, already formatted ("8:02 AM"), for
   * the stale note. Optional: an undated note is honest, an invented time is
   * not.
   */
  retainedAt?: string | null;
  /** Override when the screen can say something specific about the refusal. */
  refused?: ReactNode;
  children: ReactNode;
}

export function ListState({
  phase,
  surface = 'table',
  rows = 5,
  empty,
  failedMessage,
  onRetry,
  retainedAt = null,
  refused,
  children,
}: ListStateProps) {
  switch (phase.kind) {
    case 'loading':
      return <SkeletonSet surface={surface} rows={rows} />;
    case 'refused':
      // No `onRetry`. Asking again cannot change the answer, and a button that
      // implies it can is worse than no button.
      return <>{refused ?? <Refused />}</>;
    case 'failed':
      // `LoadFailure` rather than `ErrorPanel` for the 401 this switch cannot
      // see: `listPhase` routes 403 to `refused` above, but an expired session
      // lands here and "we couldn't load it just now — try again" is the same
      // false promise in the other key. It renders identically for every
      // failure that IS retryable.
      return <LoadFailure error={phase.error} message={failedMessage} onRetry={onRetry} />;
    case 'empty':
      return <>{empty}</>;
    case 'stale':
      // "Renders the rows untouched" only says what to do when there ARE rows.
      // When the retained answer was empty, the untouched rendering of it is
      // the empty state — not `children`, which over zero rows is a heading
      // above nothing and reads as a broken screen.
      return (
        <>
          <StaleNote asOf={retainedAt} onRetry={onRetry} />
          {phase.retainedEmpty ? empty : children}
        </>
      );
    case 'ready':
      return <>{children}</>;
  }
}
